/**
 * CRM-T.5: Email Sync State — tracks per-tenant sync progress for MS Graph / Gmail
 *
 * One row per tenant+provider. Stores last sync timestamp, cursor, error count.
 * Emails are logged as activities (type: "email_synced") — no separate email store.
 */
import {
  pgTable, uuid, text, timestamp, integer, boolean, jsonb,
  uniqueIndex, index,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const EMAIL_SYNC_PROVIDERS = ["microsoft", "google"] as const;
export type EmailSyncProvider = (typeof EMAIL_SYNC_PROVIDERS)[number];

export const emailSyncState = pgTable(
  "email_sync_state",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    provider: text("provider").notNull(), // "microsoft" | "google"
    userId: text("user_id").notNull(), // which user connected
    oauthIntegrationId: uuid("oauth_integration_id").notNull(),
    isEnabled: boolean("is_enabled").notNull().default(true),
    /** ISO timestamp of last successfully synced email */
    lastSyncAt: timestamp("last_sync_at", tz),
    /** MS Graph deltaLink or Gmail historyId for incremental sync */
    syncCursor: text("sync_cursor"),
    /** Total emails synced since connection */
    totalSynced: integer("total_synced").notNull().default(0),
    /** Total emails matched to CRM contacts */
    totalMatched: integer("total_matched").notNull().default(0),
    /** Consecutive failure count (reset on success) */
    failureCount: integer("failure_count").notNull().default(0),
    lastError: text("last_error"),
    lastErrorAt: timestamp("last_error_at", tz),
    /** Sync config: which folders, exclude patterns, etc. */
    config: jsonb("config").$type<{
      syncInbox?: boolean;
      syncSent?: boolean;
      excludePatterns?: string[];
      aiSummarize?: boolean;
    }>().default({ syncInbox: true, syncSent: true, aiSummarize: true }),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_email_sync_tenant_provider_user").on(table.tenantId, table.provider, table.userId),
    index("idx_email_sync_tenant").on(table.tenantId),
    index("idx_email_sync_enabled").on(table.isEnabled, table.lastSyncAt),
  ],
);
